// What the voucher is printed ON.
//
// The console used to assume one kind of paper: an A4 sheet out of an office
// laser printer. That is not what a check-in counter has. The printer standing
// next to the agent at the desk is the one that prints air tickets and boarding
// passes — an ATB unit loaded with IATA Resolution 722c coupon stock (8 × 3¼ in),
// or a thermal roll printer — and both of them ignore an A4 layout: the ATB
// clips everything past the first 82 mm of the page, the roll printer feeds a
// whole sheet's worth of blank paper per voucher. So the sheet has to be told
// which stock it is going to, and the operator is the only one who knows.
//
// TWO THINGS PER FORMAT, AND THEY ARE DIFFERENT THINGS. `pageSize` / `pageMargin`
// tell the PRINTER the geometry, through an injected `@page` rule — CSS has no
// way to scope `@page` to an element, so a class on <body> cannot do it and a
// <style> tag has to be written at print time. `widthMm` / `heightMm` tell the
// SCREEN what to draw, in the same physical millimetres, so the preview in the
// dialog is the coupon at 1:1 rather than a guess at it.
//
// i18n: this module is not a component, so it holds translation KEYS, never
// language. Every operator-facing caption on a format (`labelKey`,
// `sizeLabelKey`, `hintKey`, `driverHintKey`) resolves in the `pnrActions`
// namespace at the call site that draws the picker.
//
// WE CANNOT PICK THE PRINTER. No browser lets a page choose a destination — that
// is the OS dialog's job, on purpose. What we can do is hand the operator the
// right geometry and say plainly what to set over there (`driverHintKey`), because
// the two settings that actually ruin ticket stock — "Fit to page" scaling and
// default margins — are both in that dialog and both default to wrong.

export type PrintFormatId = 'a4' | 'letter' | 'atb' | 'thermal80';

/**
 * The shape of the paper, which decides the LAYOUT rather than the size.
 *
 * `document` is a page you read holding it in two hands; `ticket` is a coupon
 * torn off a stack, where every coupon has to stand on its own because they get
 * separated the moment they leave the desk; `receipt` is an endless narrow
 * column where vertical space is free and horizontal space is nearly gone.
 */
export type PrintStock = 'document' | 'ticket' | 'receipt';

export interface PrintFormatSpec {
  id: PrintFormatId;
  /** How the operator names this paper out loud, as a `pnrActions` key. */
  labelKey: string;
  /** The size, as printed on the box the stock came in — a `pnrActions` key. */
  sizeLabelKey: string;
  stock: PrintStock;
  /** `@page { size: … }` — see the note on `thermal80` below. */
  pageSize: string;
  /** `@page { margin: … }`. Ticket stock has no margin to give away. */
  pageMargin: string;
  /** Printable width in mm — the preview draws at exactly this. */
  widthMm: number;
  /** Printable height of ONE page in mm; `null` = continuous roll. */
  heightMm: number | null;
  /** What this stock is, in one line, for the operator choosing it — a key. */
  hintKey: string;
  /** What to set in the browser's own print dialog, which we cannot set — a key. */
  driverHintKey: string;
}

/**
 * The four papers, in the order an operator meets them.
 *
 * ATB is the 722c coupon: 8.0 × 3.25 in = 203.2 × 82.55 mm, landscape, the exact
 * stock an airline ticket printer is loaded with. Zero margin — the printer's own
 * unprintable edge is already inside those numbers, and asking for 14 mm on top
 * of it loses a third of the coupon.
 *
 * `thermal80` asks for `80mm auto`: a roll has no page height, and naming one
 * makes the printer feed (and cut) that much paper whatever the voucher weighs.
 * A browser that will not take `auto` there falls back to its default page —
 * degraded, not broken, and the roll length in the driver still wins.
 */
export const A4_FORMAT: PrintFormatSpec = {
  id: 'a4',
  labelKey: 'print.format.a4.label',
  sizeLabelKey: 'print.format.a4.size',
  stock: 'document',
  pageSize: 'A4',
  pageMargin: '14mm',
  widthMm: 182,
  heightMm: 269,
  hintKey: 'print.format.a4.hint',
  driverHintKey: 'print.format.a4.driver',
};

const LETTER_FORMAT: PrintFormatSpec = {
  id: 'letter',
  labelKey: 'print.format.letter.label',
  sizeLabelKey: 'print.format.letter.size',
  stock: 'document',
  pageSize: 'Letter',
  pageMargin: '14mm',
  widthMm: 188,
  heightMm: 251,
  hintKey: 'print.format.letter.hint',
  driverHintKey: 'print.format.letter.driver',
};

const ATB_FORMAT: PrintFormatSpec = {
  id: 'atb',
  labelKey: 'print.format.atb.label',
  sizeLabelKey: 'print.format.atb.size',
  stock: 'ticket',
  pageSize: '203.2mm 82.55mm',
  pageMargin: '0',
  widthMm: 203.2,
  heightMm: 82.55,
  hintKey: 'print.format.atb.hint',
  driverHintKey: 'print.format.atb.driver',
};

const THERMAL80_FORMAT: PrintFormatSpec = {
  id: 'thermal80',
  labelKey: 'print.format.thermal80.label',
  sizeLabelKey: 'print.format.thermal80.size',
  stock: 'receipt',
  pageSize: '80mm auto',
  pageMargin: '0',
  widthMm: 80,
  heightMm: null,
  hintKey: 'print.format.thermal80.hint',
  driverHintKey: 'print.format.thermal80.driver',
};

export const PRINT_FORMATS: readonly PrintFormatSpec[] = [
  A4_FORMAT,
  LETTER_FORMAT,
  ATB_FORMAT,
  THERMAL80_FORMAT,
];

export const DEFAULT_PRINT_FORMAT: PrintFormatId = 'a4';

export function getPrintFormat(id: PrintFormatId | undefined): PrintFormatSpec {
  return PRINT_FORMATS.find((f) => f.id === id) ?? A4_FORMAT;
}

/** Ticket and receipt stock share one narrow, monospaced, coupon-shaped renderer. */
export function isCouponStock(format: PrintFormatSpec): boolean {
  return format.stock === 'ticket' || format.stock === 'receipt';
}

const STORAGE_KEY = 'nexa.print.format';

/**
 * The last stock this machine printed on.
 *
 * A counter position does not alternate: the desk with the ATB printer prints
 * ATB all shift, the back office prints A4 all shift. Remembering the choice per
 * browser turns "ask the operator" into one glance at a preselected button
 * instead of a decision they have to make forty times a night — while still
 * being a choice they SEE, which is the part that stops A4 markup being fired at
 * a stack of ticket stock.
 *
 * Storage is wrapped: Safari private mode throws on both read and write, and a
 * voucher must never fail to open because a preference could not be recalled.
 */
export function readPrintFormat(): PrintFormatId {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const match = PRINT_FORMATS.find((f) => f.id === raw);
    return match ? match.id : DEFAULT_PRINT_FORMAT;
  } catch {
    return DEFAULT_PRINT_FORMAT;
  }
}

export function writePrintFormat(id: PrintFormatId): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* preference is a convenience, never a precondition */
  }
}

const PAGE_STYLE_ID = 'nexa-print-page-rule';

/**
 * Tell the printer the geometry, for as long as the sheet is open.
 *
 * `@page` is not selector-scoped — there is no way to write "this page size when
 * that element is on screen" — so the only way to switch geometry at runtime is
 * to write the rule into the document and take it out again. Appending to
 * <head> puts it after the app stylesheet, so it beats the console-wide
 * `@page { margin: 14mm }` in index.css by cascade order; removing it on cleanup
 * puts Ctrl+P anywhere else in the console back exactly as it was.
 *
 * Returns its own undo, so callers can hand it straight to a React effect.
 */
export function applyPrintPageRule(format: PrintFormatSpec): () => void {
  if (typeof document === 'undefined') return () => {};
  let style = document.getElementById(PAGE_STYLE_ID) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = PAGE_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = `@page { size: ${format.pageSize}; margin: ${format.pageMargin}; }`;
  return () => document.getElementById(PAGE_STYLE_ID)?.remove();
}
